// IndexedDB-backed store for mutations queued against a pending photo —
// tag toggles, issue-status changes, markup saves — that the user makes
// while the photo is still in the upload queue (pendingPhotoStore).
//
// One row per local_id; each row carries optional fields for the three
// kinds. Last-write-wins per kind. When the photo's upload pipeline
// runs (photoUploadQueue.runPipeline), it reads this row and applies
// the mutations server-side after the photos INSERT lands, then clears
// the entry.
//
// Storage shape: nothing tricky here (no Blobs), so v1 keeps it simple.
// Schema separated from pendingPhotoStore so we can iterate on either
// without database-version coupling.
//
// Note: there are NO server tags/markup/status for a pending photo,
// since the row doesn't exist server-side yet. So queued state IS the
// authoritative state for a pending photo.
//
// Anything that mutates the store calls notify() so React hooks can re-read.
// Hooks share a single cached snapshot via the pendingMutations module
// in usePendingPhotos.ts.
import type { IssueStatus, MarkupData } from '../types/database'

export interface PendingMutations {
  local_id: string
  tag_ids?: string[]          // full set; merging done in store helpers below
  issue_status?: IssueStatus
  markup_data?: MarkupData
}

const DB_NAME = 'punch-pending-mutations'
const DB_VERSION = 1
const STORE = 'mutations'

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'local_id' })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return dbPromise
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then(db => db.transaction(STORE, mode).objectStore(STORE))
}

// ── subscribers ──────────────────────────────────────────────────────────────

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => { listeners.delete(fn) }
}

function notify() {
  for (const fn of listeners) {
    try { fn() } catch (err) { console.error('pendingMutationStore listener error:', err) }
  }
}

// ── reads ────────────────────────────────────────────────────────────────────

export async function getAll(): Promise<PendingMutations[]> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.getAll()
    req.onsuccess = () => resolve(req.result as PendingMutations[])
    req.onerror = () => reject(req.error)
  })
}

export async function get(localId: string): Promise<PendingMutations | null> {
  const store = await tx('readonly')
  return new Promise((resolve, reject) => {
    const req = store.get(localId)
    req.onsuccess = () => resolve((req.result as PendingMutations | undefined) ?? null)
    req.onerror = () => reject(req.error)
  })
}

// ── writes ───────────────────────────────────────────────────────────────────

async function putRow(row: PendingMutations): Promise<void> {
  const store = await tx('readwrite')
  await new Promise<void>((resolve, reject) => {
    const req = store.put(row)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
  notify()
}

export async function setIssueStatus(localId: string, status: IssueStatus): Promise<void> {
  const existing = (await get(localId)) ?? { local_id: localId }
  await putRow({ ...existing, issue_status: status })
}

export async function setMarkup(localId: string, data: MarkupData): Promise<void> {
  const existing = (await get(localId)) ?? { local_id: localId }
  await putRow({ ...existing, markup_data: data })
}

export async function setTags(localId: string, tagIds: string[]): Promise<void> {
  const existing = (await get(localId)) ?? { local_id: localId }
  await putRow({ ...existing, tag_ids: tagIds })
}

export async function addTag(localId: string, tagId: string): Promise<void> {
  const existing = (await get(localId)) ?? { local_id: localId }
  const current = existing.tag_ids ?? []
  if (current.includes(tagId)) return
  await putRow({ ...existing, tag_ids: [...current, tagId] })
}

export async function removeTag(localId: string, tagId: string): Promise<void> {
  const existing = (await get(localId)) ?? { local_id: localId }
  const current = existing.tag_ids ?? []
  if (!current.includes(tagId)) return
  await putRow({ ...existing, tag_ids: current.filter(id => id !== tagId) })
}

export async function clearTags(localId: string): Promise<void> {
  const existing = await get(localId)
  if (!existing) return
  await putRow({ ...existing, tag_ids: [] })
}

// Remove the entire row — called by the upload pipeline after queued
// mutations have been applied server-side.
export async function clear(localId: string): Promise<void> {
  const store = await tx('readwrite')
  await new Promise<void>((resolve, reject) => {
    const req = store.delete(localId)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
  notify()
}
